import { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Calendar, Clock, Users, User, MapPin, Ticket, PartyPopper,
  Search, Check, X, Loader2, ChevronLeft, ChevronRight,
  CreditCard, Gift, Share2, Heart, Star, Music, Utensils, Wine,
  Phone, Mail, Send, AlertCircle, Info
} from 'lucide-react';

export default function EventWidget({
  restaurantId,
  restaurantName = 'Restaurante',
  logoUrl,
  primaryColor = '#F97316',
  onSuccess,
  onClose,
  eventId = null,
  API_URL = process.env.REACT_APP_BACKEND_URL,
}) {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  // Events list
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  // Ticket selection
  const [tickets, setTickets] = useState({});
  const [totalPrice, setTotalPrice] = useState(0);
  
  // Guest info
  const [guestType, setGuestType] = useState('self');
  const [okUserNickname, setOkUserNickname] = useState('');
  const [okUserFound, setOkUserFound] = useState(null);
  const [searchingUser, setSearchingUser] = useState(false);
  const [guestInfo, setGuestInfo] = useState({ name: '', email: '', phone: '' });
  
  // Load events
  useEffect(() => {
    fetchEvents();
  }, [restaurantId]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/events/public/restaurant/${restaurantId}`);
      if (response.ok) {
        const data = await response.json();
        setEvents(data);
        // If eventId provided, select that event
        if (eventId) {
          const event = data.find(e => e.id === eventId);
          if (event) {
            setSelectedEvent(event);
            initTickets(event);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Error al cargar eventos');
    } finally {
      setLoading(false);
    }
  };

  const initTickets = (event) => {
    const ticketTypes = event.ticket_types || [{ id: 'general', name: 'Entrada General', price: 0 }];
    const initialTickets = {};
    ticketTypes.forEach(t => {
      initialTickets[t.id] = 0;
    });
    setTickets(initialTickets);
  };

  const selectEvent = (event) => {
    setSelectedEvent(event);
    initTickets(event);
    setCurrentStep(2);
  };

  const updateTicketCount = (ticketId, delta) => {
    setTickets(prev => {
      const newCount = Math.max(0, Math.min(10, (prev[ticketId] || 0) + delta));
      return { ...prev, [ticketId]: newCount };
    });
  };

  // Calculate total price
  useEffect(() => {
    if (selectedEvent) {
      const ticketTypes = selectedEvent.ticket_types || [{ id: 'general', name: 'Entrada General', price: 0 }];
      let total = 0;
      ticketTypes.forEach(t => {
        total += (tickets[t.id] || 0) * t.price;
      });
      setTotalPrice(total);
    }
  }, [tickets, selectedEvent]);

  // Search OkUser
  const searchOkUser = async (nickname) => {
    if (!nickname || nickname.length < 2) return;
    
    setSearchingUser(true);
    try {
      const response = await fetch(`${API_URL}/api/clients/search?nickname=${encodeURIComponent(nickname)}`);
      if (response.ok) {
        const data = await response.json();
        if (data && data.length > 0) {
          setOkUserFound(data[0]);
        } else {
          setOkUserFound(null);
        }
      }
    } catch (err) {
      console.error('Error searching user:', err);
    } finally {
      setSearchingUser(false);
    }
  };

  // Total tickets count
  const totalTickets = Object.values(tickets).reduce((a, b) => a + b, 0);

  // Can proceed validation
  const canProceed = () => {
    if (currentStep === 2) {
      return totalTickets > 0;
    }
    if (currentStep === 3) {
      if (guestType === 'self') return okUserFound !== null;
      return guestInfo.name && guestInfo.phone;
    }
    return true;
  };

  // Submit reservation
  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    
    try {
      const ticketDetails = [];
      const ticketTypes = selectedEvent.ticket_types || [{ id: 'general', name: 'Entrada General', price: 0 }];
      
      ticketTypes.forEach(t => {
        if (tickets[t.id] > 0) {
          ticketDetails.push({
            ticket_type_id: t.id,
            ticket_type_name: t.name,
            quantity: tickets[t.id],
            unit_price: t.price,
            subtotal: tickets[t.id] * t.price
          });
        }
      });

      const reservationData = {
        event_id: selectedEvent.id,
        restaurant_id: restaurantId,
        guest_name: guestType === 'self' ? okUserFound?.name : guestInfo.name,
        guest_email: guestType === 'self' ? '' : guestInfo.email,
        guest_phone: guestType === 'self' ? '' : guestInfo.phone,
        okuser_id: guestType === 'self' ? okUserFound?.id : null,
        tickets: ticketDetails,
        total_tickets: totalTickets,
        total_price: totalPrice,
        source: 'widget'
      };

      const response = await fetch(`${API_URL}/api/events/reservations/widget`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reservationData),
      });

      if (response.ok) {
        const result = await response.json();
        setCurrentStep(4);
        onSuccess?.(result);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Error al crear la reservación');
      }
    } catch (err) {
      setError('Error de conexión. Intenta de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  // Format price
  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(price);
  };

  return (
    <div className="w-full max-w-md mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden">
      {/* Header */}
      <div 
        className="px-6 py-6 text-center"
        style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}dd)` }}
      >
        {logoUrl ? (
          <img 
            src={logoUrl} 
            alt={restaurantName} 
            className="w-24 h-24 mx-auto object-contain rounded-2xl bg-white/10 p-2"
          />
        ) : (
          <div className="w-24 h-24 mx-auto bg-white/20 rounded-2xl flex items-center justify-center">
            <PartyPopper className="w-12 h-12 text-white" />
          </div>
        )}
        <h1 className="text-xl font-bold text-white mt-3">{restaurantName}</h1>
        <p className="text-white/70 text-sm">Eventos</p>
        
        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mt-4">
          {[1, 2, 3, 4].map((step) => (
            <div
              key={step}
              className={`h-2 rounded-full transition-all ${
                step === currentStep 
                  ? 'w-8 bg-white' 
                  : step < currentStep 
                    ? 'w-4 bg-white/70' 
                    : 'w-4 bg-white/30'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6 max-h-[60vh] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : error && currentStep === 1 ? (
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 mx-auto text-red-400 mb-3" />
            <p className="text-gray-600">{error}</p>
          </div>
        ) : (
          <>
            {/* Step 1: Event Selection */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                  <Calendar className="w-5 h-5" style={{ color: primaryColor }} />
                  Próximos Eventos
                </h3>
                
                {events.length === 0 ? (
                  <div className="text-center py-8">
                    <PartyPopper className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500">No hay eventos programados</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {events.map((event) => (
                      <button
                        key={event.id}
                        onClick={() => selectEvent(event)}
                        className="w-full p-4 rounded-2xl border-2 border-gray-200 hover:border-orange-400 transition-all text-left"
                      >
                        <div className="flex gap-4">
                          {event.image_url ? (
                            <img 
                              src={event.image_url} 
                              alt={event.name}
                              className="w-20 h-20 rounded-xl object-cover"
                            />
                          ) : (
                            <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center">
                              <Music className="w-8 h-8 text-orange-500" />
                            </div>
                          )}
                          <div className="flex-1">
                            <h4 className="font-bold text-gray-900">{event.name}</h4>
                            <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {event.date && format(parseISO(event.date), "EEEE d 'de' MMMM", { locale: es })}
                            </p>
                            <p className="text-sm text-gray-500 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {event.time || '20:00'}
                            </p>
                            {event.ticket_types && event.ticket_types[0]?.price > 0 && (
                              <p className="text-sm font-semibold mt-1" style={{ color: primaryColor }}>
                                Desde {formatPrice(event.ticket_types[0].price)}
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Ticket Selection */}
            {currentStep === 2 && selectedEvent && (
              <div className="space-y-6">
                {/* Event Summary */}
                <div className="p-4 bg-gray-50 rounded-2xl">
                  <h3 className="font-bold text-gray-900">{selectedEvent.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {selectedEvent.date && format(parseISO(selectedEvent.date), "EEEE d 'de' MMMM", { locale: es })} • {selectedEvent.time || '20:00'}
                  </p>
                  {selectedEvent.description && (
                    <p className="text-sm text-gray-600 mt-2">{selectedEvent.description}</p>
                  )}
                </div>

                {/* Ticket Types */}
                <div>
                  <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Ticket className="w-5 h-5" style={{ color: primaryColor }} />
                    Selecciona tus boletos
                  </h4>
                  
                  <div className="space-y-3">
                    {(selectedEvent.ticket_types || [{ id: 'general', name: 'Entrada General', price: 0, description: 'Acceso general al evento' }]).map((ticket) => (
                      <div key={ticket.id} className="p-4 border-2 border-gray-200 rounded-xl">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h5 className="font-semibold text-gray-900">{ticket.name}</h5>
                            {ticket.description && (
                              <p className="text-xs text-gray-500 mt-1">{ticket.description}</p>
                            )}
                          </div>
                          <span className="font-bold" style={{ color: primaryColor }}>
                            {ticket.price > 0 ? formatPrice(ticket.price) : 'Gratis'}
                          </span>
                        </div>
                        
                        <div className="flex items-center justify-center gap-4">
                          <button
                            onClick={() => updateTicketCount(ticket.id, -1)}
                            className="w-10 h-10 rounded-full border-2 border-gray-200 flex items-center justify-center font-bold text-gray-600 hover:bg-gray-100"
                          >
                            -
                          </button>
                          <span className="text-2xl font-bold w-12 text-center" style={{ color: primaryColor }}>
                            {tickets[ticket.id] || 0}
                          </span>
                          <button
                            onClick={() => updateTicketCount(ticket.id, 1)}
                            className="w-10 h-10 rounded-full border-2 border-gray-200 flex items-center justify-center font-bold text-gray-600 hover:bg-gray-100"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Total */}
                {totalTickets > 0 && (
                  <div className="p-4 rounded-xl" style={{ backgroundColor: `${primaryColor}10` }}>
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-700">Total ({totalTickets} boleto{totalTickets !== 1 ? 's' : ''})</span>
                      <span className="text-xl font-bold" style={{ color: primaryColor }}>
                        {totalPrice > 0 ? formatPrice(totalPrice) : 'Gratis'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Guest Info */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                  <User className="w-5 h-5" style={{ color: primaryColor }} />
                  ¿A nombre de quién?
                </h3>

                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setGuestType('self')}
                    className={`p-3 rounded-xl border-2 text-center transition-all ${
                      guestType === 'self' 
                        ? 'border-orange-500 bg-orange-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    style={guestType === 'self' ? { borderColor: primaryColor, backgroundColor: `${primaryColor}10` } : {}}
                  >
                    <User className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-sm font-medium">Usuario OkGo</span>
                  </button>
                  <button
                    onClick={() => setGuestType('external')}
                    className={`p-3 rounded-xl border-2 text-center transition-all ${
                      guestType === 'external' 
                        ? 'border-orange-500 bg-orange-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    style={guestType === 'external' ? { borderColor: primaryColor, backgroundColor: `${primaryColor}10` } : {}}
                  >
                    <Users className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-sm font-medium">Otro</span>
                  </button>
                </div>

                {guestType === 'self' ? (
                  <div className="space-y-2">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Tu @nickname de OkGo"
                        value={okUserNickname}
                        onChange={(e) => {
                          setOkUserNickname(e.target.value);
                          setOkUserFound(null);
                        }}
                        onBlur={() => searchOkUser(okUserNickname)}
                        className="w-full px-4 py-3 pl-10 border border-gray-200 rounded-xl focus:outline-none focus:ring-2"
                      />
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      {searchingUser && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 animate-spin" />
                      )}
                    </div>
                    {okUserFound && (
                      <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl border border-green-200">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center overflow-hidden">
                          {okUserFound.photo_url ? (
                            <img src={okUserFound.photo_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <User className="w-5 h-5 text-green-600" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-green-800">{okUserFound.name}</p>
                          <p className="text-xs text-green-600">@{okUserFound.nickname}</p>
                        </div>
                        <Check className="w-5 h-5 text-green-600" />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <input
                      type="text"
                      placeholder="Nombre completo"
                      value={guestInfo.name}
                      onChange={(e) => setGuestInfo({ ...guestInfo, name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2"
                    />
                    <input
                      type="email"
                      placeholder="Correo electrónico"
                      value={guestInfo.email}
                      onChange={(e) => setGuestInfo({ ...guestInfo, email: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2"
                    />
                    <input
                      type="tel"
                      placeholder="Teléfono / WhatsApp"
                      value={guestInfo.phone}
                      onChange={(e) => setGuestInfo({ ...guestInfo, phone: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2"
                    />
                  </div>
                )}

                {/* Summary */}
                <div className="p-4 bg-gray-50 rounded-xl space-y-2">
                  <h4 className="font-medium text-gray-700 mb-2">Resumen</h4>
                  <div className="text-sm flex justify-between">
                    <span className="text-gray-500">Evento</span>
                    <span className="font-medium">{selectedEvent?.name}</span>
                  </div>
                  <div className="text-sm flex justify-between">
                    <span className="text-gray-500">Boletos</span>
                    <span className="font-medium">{totalTickets}</span>
                  </div>
                  <div className="text-sm flex justify-between pt-2 border-t">
                    <span className="font-medium">Total</span>
                    <span className="font-bold" style={{ color: primaryColor }}>
                      {totalPrice > 0 ? formatPrice(totalPrice) : 'Gratis'}
                    </span>
                  </div>
                </div>

                {error && (
                  <div className="p-4 bg-red-50 rounded-xl border border-red-200 text-red-700 text-sm">
                    {error}
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Confirmation */}
            {currentStep === 4 && (
              <div className="text-center space-y-6 py-4">
                <div 
                  className="w-20 h-20 rounded-full mx-auto flex items-center justify-center"
                  style={{ backgroundColor: `${primaryColor}20` }}
                >
                  <Check className="w-10 h-10" style={{ color: primaryColor }} />
                </div>
                
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">¡Registro Confirmado!</h3>
                  <p className="text-gray-500 text-sm">
                    Te hemos enviado un correo con los detalles
                  </p>
                </div>

                <div className="p-4 bg-gray-50 rounded-xl text-left space-y-2">
                  <div className="text-sm flex justify-between">
                    <span className="text-gray-500">Evento</span>
                    <span className="font-medium">{selectedEvent?.name}</span>
                  </div>
                  <div className="text-sm flex justify-between">
                    <span className="text-gray-500">Fecha</span>
                    <span className="font-medium">
                      {selectedEvent?.date && format(parseISO(selectedEvent.date), "d 'de' MMMM", { locale: es })}
                    </span>
                  </div>
                  <div className="text-sm flex justify-between">
                    <span className="text-gray-500">Boletos</span>
                    <span className="font-medium">{totalTickets}</span>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      // Share functionality
                      if (navigator.share) {
                        navigator.share({
                          title: selectedEvent?.name,
                          text: `¡Asiste a ${selectedEvent?.name}!`,
                          url: window.location.href
                        });
                      }
                    }}
                    className="flex-1 py-3 rounded-xl border-2 border-gray-200 font-medium flex items-center justify-center gap-2 hover:bg-gray-50"
                  >
                    <Share2 className="w-4 h-4" />
                    Compartir
                  </button>
                  <button
                    onClick={() => window.location.reload()}
                    className="flex-1 py-3 rounded-xl text-white font-medium"
                    style={{ backgroundColor: primaryColor }}
                  >
                    Otro Evento
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer with navigation */}
      {currentStep > 1 && currentStep < 4 && (
        <div className="p-6 border-t bg-gray-50">
          <div className="flex gap-3">
            <button
              onClick={() => setCurrentStep(currentStep - 1)}
              className="flex-1 py-4 border-2 border-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
            >
              <ChevronLeft className="w-5 h-5" />
              Atrás
            </button>
            
            {currentStep < 3 ? (
              <button
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={!canProceed()}
                className="flex-1 py-4 text-white rounded-xl font-bold transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: primaryColor }}
              >
                Siguiente
                <ChevronRight className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting || !canProceed()}
                className="flex-1 py-4 text-white rounded-xl font-bold transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: primaryColor }}
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Ticket className="w-5 h-5" />
                    Confirmar
                  </>
                )}
              </button>
            )}
          </div>
          
          {/* Footer branding */}
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-400">
              Powered by <span className="font-semibold text-orange-500">OkGo</span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
